import { OptionType, Some } from 'ng/src/app/core/Option';

export type HasValueOrProvider<T> = HasValue<T> | (() => T);

export namespace HasValueOrProvider {
	export function get<T>(val: HasValueOrProvider<T>): T {
		return typeof val === 'function' ? val() : val.value;
	}
}

export interface HasValue<T> {
	value: T;
}

export interface DerivedValueTransformOptions<T, E> {
	get: (val: T) => E;
	set: (newVal: E, val: T) => T;
}

export interface ComputedValueOptions<T, E> {
	// A reference to the value that is used to compute the new value
	reference: HasValueOrProvider<E>,
	// A function that computes the new value based on the reference value. Provided
	// the reference value and the previous computed value.
	get: (val: E, previousValue: OptionType<T>) => T;
}

abstract class ValueAliasBase<T, EventType extends string, Type extends ValueAliasBase<T, EventType, any>> implements HasValue<T>{
	protected abstract callbackMap: Record<EventType, Set<(val: Type) => void>>
	
	get value() {
		return this.getter();
	}
	
	set value(val: T) {
		this.setter(val);
	}
	
	constructor(
		protected getter: () => T,
		protected setter: (val: T) => void
	) {}
	
	addEventListener(type: EventType, callback: (value: Type) => void) {
		this.callbackMap[type].add(callback);
		return this;
	}
	
	removeEventListener(type: EventType, callback: (value: Type) => void) {
		this.callbackMap[type].delete(callback);
		return this;
	}
	
	clearEventListeners() {
		Object.values(this.callbackMap).forEach((set) => {
			(set as Set<any>).clear()
		});
		return this;
	}
	
	derived<E>(transform: DerivedValueTransformOptions<T, E>): DerivedValueAlias<T, E> {
		return new DerivedValueAlias<T, E>(this, transform);
	}
	
	protected callEvents(type: EventType) {
		const self = this as any;
		this.callbackMap[type].forEach(fn => fn(self));
	}
}

// A ValueAlias that can be used to track changes to a value. Useful for tracking changes if needed.
export class ValueAlias<T> extends ValueAliasBase<T, 'change', ValueAlias<T>>{
	protected callbackMap: Record<"change", Set<(val: ValueAlias<T>) => void>> = {
		change: new Set()
	};
	
	static of<T, K extends keyof T>(obj: T, key: K): ValueAlias<T[K]> {
		return new ValueAlias<T[K]>(() => obj[key], val => obj[key] = val);
	}
	
	static readOnly<T, K extends keyof T>(obj: T, key: K): ValueAlias<T[K]> {
		return new ValueAlias<T[K]>(() => obj[key], () => {});
	}
}

// A ValueAlias that can be rolled back to a saved state. Useful for tracking a change and rolling it back to a specific point if needed.
export class RollbackValueAlias<T> extends ValueAliasBase<T, 'change' | 'save' | 'rollback', RollbackValueAlias<T>> {

	static of<T, K extends keyof T>(src: T, key: K): RollbackValueAlias<T[K]> {
		return new RollbackValueAlias<T[K]>(() => src[key], val => src[key] = val);
	}
	
	protected callbackMap: Record<"change" | "save" | "rollback", Set<(val: RollbackValueAlias<T>) => void>> = {
		change: new Set(),
		save: new Set(),
		rollback: new Set()
	};

	private savedValue: T
	
	constructor(
		getter: () => T,
		setter: (val: T) => void
	) {
		super(getter, setter);
		this.savedValue = this.value;
	}
	
	saveState() {
		this.savedValue = this.value;
		this.callEvents('save');
	}
	
	rollback() {
		this.value = this.savedValue;
		this.callEvents('rollback');
	}

	hasChanges() {
		return this.value !== this.savedValue;
	}
}

// A ValueAlias that can be committed or discarded. Useful for tracking a change and throwing it away if needed.
export class CommitableValueAlias<T> extends ValueAliasBase<T, 'commit' | 'discard' | 'change', CommitableValueAlias<T>> {
	
	static of<T, K extends keyof T>(obj: T, key: K): CommitableValueAlias<T[K]> {
		return new CommitableValueAlias<T[K]>(() => obj[key], val => obj[key] = val);
	}
	
	protected _hasValueInProgress = false;
	protected _inProgress?: T;
	protected callbackMap: Record<'commit' | 'discard' | 'change', Set<(value: CommitableValueAlias<T>) => void>> = {
		commit: new Set(),
		discard: new Set(),
		change: new Set()
	}
	
	get value() {
		return this._hasValueInProgress ? this._inProgress! : this.getter();
	}
	
	set value(val) {
		this._hasValueInProgress = true;
		this._inProgress = val;
		this.callEvents('change');
	}
	
	hasChanges() {
		return this._hasValueInProgress;
	}
	
	commit() {
		if (!this._hasValueInProgress) return;
		this.setter(this._inProgress!);
		this.clearInProgress();
		this.callEvents('commit');
	}
	
	discard() {
		this.clearInProgress();
		this.callEvents('discard');
	}
	
	protected clearInProgress() {
		this._hasValueInProgress = false;
		delete this._inProgress;
	}
}

/**
 * A Value that is derived from another Value. It is used to transform a value into another value through transformation functions.
 */
export class DerivedValueAlias<T, E> implements HasValue<E> {
	
	static readOnly<T, E>(value: HasValue<T>, transform: DerivedValueTransformOptions<T, E>['get']) {
		const alias = new ValueAlias<T>(
			() => value.value,
			() => {}
		);
		return new DerivedValueAlias<T, E>(alias, {
			get: transform,
			set: (_, t) => t
		});
	}
	
	constructor(
		private alias: HasValue<T>,
		private transform: DerivedValueTransformOptions<T, E>
	) {}
	
	get value() {
		return this.transform.get(this.alias.value);
	}
	
	set value(val: E) {
		this.alias.value = this.transform.set(val, this.alias.value);
	}
}

/**
 * A value that is derived from another source. It is computed and cached while the original reference is still equivalent.
 */
export class ComputedValue<T, E> implements Readonly<HasValue<T>> {
	
	constructor(
		computedValueOptions: ComputedValueOptions<T, E>,
	) {
		this.options = { ...computedValueOptions };
	}
	
	private options: ComputedValueOptions<T, E>;
	private lastReference = OptionType.none<E>().infer;
	private computedValue = OptionType.none<T>().infer;
	
	get value(): T {
		let result: Some<T>;
		const ref = OptionType.of(HasValueOrProvider.get(this.options.reference));
		
		const getNewValue = (ref: E) => {
			return this.options.get(ref, this.computedValue);
		}
		
		if (this.lastReference.isSome()) {
			if (this.lastReference.value !== ref.value) {
				result = OptionType.of(getNewValue(ref.value));
				this.lastReference = ref;
			}
			else if (this.computedValue.isSome()) {
				result = this.computedValue;
			}
			else {
				result = this.computedValue.orElse(() => getNewValue(ref.value));
			}
		}
		else {
			result = OptionType.of(getNewValue(ref.value));
			this.lastReference = ref;
		}
		
		this.computedValue = result;
		return result.value;
	}
}